/**
 * The room's language: one BCP 47 code (`es`, `ta`, `pt-BR`) kept on the room. Everything written from the talk
 * and everything the page says in its own words follows it. This file is the one list of what a code means: its
 * name in its own words, its script (for the font), its direction, and the few fixed words the page needs.
 *
 * Plus Jakarta Sans coverage checked on Google Fonts' live metadata (fonts.google.com/metadata/fonts,
 * 2026-10-27): its subsets are latin, latin-ext, vietnamese and cyrillic-ext. Plain Cyrillic and Greek are not in
 * it, so those take Noto Sans.
 */
export const DEFAULT_LANGUAGE = "en";

export type Script =
  | "latin" | "cyrillic" | "greek" | "arabic" | "hebrew" | "devanagari" | "bengali" | "tamil" | "telugu"
  | "kannada" | "malayalam" | "gujarati" | "gurmukhi" | "oriya" | "sinhala" | "thai" | "lao" | "khmer"
  | "myanmar" | "georgian" | "armenian" | "ethiopic" | "han-sc" | "han-tc" | "japanese" | "korean";

interface Language {
  /** ISO 639-1 where there is one, else ISO 639-3. */
  code: string;
  iso3: string;
  /** The name in the language's own words, which is what the host picks by. */
  own: string;
  english: string;
  script: Script;
  rtl?: true;
}

const L = (code: string, iso3: string, own: string, english: string, script: Script, rtl?: true): Language =>
  rtl ? { code, iso3, own, english, script, rtl } : { code, iso3, own, english, script };

export const LANGUAGES: readonly Language[] = [
  L("en", "eng", "English", "English", "latin"),
  L("es", "spa", "Español", "Spanish", "latin"),
  L("fr", "fra", "Français", "French", "latin"),
  L("de", "deu", "Deutsch", "German", "latin"),
  L("pt", "por", "Português", "Portuguese", "latin"),
  L("it", "ita", "Italiano", "Italian", "latin"),
  L("nl", "nld", "Nederlands", "Dutch", "latin"),
  L("sv", "swe", "Svenska", "Swedish", "latin"),
  L("da", "dan", "Dansk", "Danish", "latin"),
  L("nb", "nob", "Norsk bokmål", "Norwegian", "latin"),
  L("fi", "fin", "Suomi", "Finnish", "latin"),
  L("pl", "pol", "Polski", "Polish", "latin"),
  L("cs", "ces", "Čeština", "Czech", "latin"),
  L("sk", "slk", "Slovenčina", "Slovak", "latin"),
  L("hu", "hun", "Magyar", "Hungarian", "latin"),
  L("ro", "ron", "Română", "Romanian", "latin"),
  L("hr", "hrv", "Hrvatski", "Croatian", "latin"),
  L("sl", "slv", "Slovenščina", "Slovenian", "latin"),
  L("ca", "cat", "Català", "Catalan", "latin"),
  L("tr", "tur", "Türkçe", "Turkish", "latin"),
  L("id", "ind", "Bahasa Indonesia", "Indonesian", "latin"),
  L("ms", "msa", "Bahasa Melayu", "Malay", "latin"),
  L("fil", "fil", "Filipino", "Filipino", "latin"),
  L("vi", "vie", "Tiếng Việt", "Vietnamese", "latin"),
  L("sw", "swa", "Kiswahili", "Swahili", "latin"),
  L("af", "afr", "Afrikaans", "Afrikaans", "latin"),
  L("ru", "rus", "Русский", "Russian", "cyrillic"),
  L("uk", "ukr", "Українська", "Ukrainian", "cyrillic"),
  L("bg", "bul", "Български", "Bulgarian", "cyrillic"),
  L("sr", "srp", "Српски", "Serbian", "cyrillic"),
  L("el", "ell", "Ελληνικά", "Greek", "greek"),
  L("ar", "ara", "العربية", "Arabic", "arabic", true),
  L("fa", "fas", "فارسی", "Persian", "arabic", true),
  L("ur", "urd", "اردو", "Urdu", "arabic", true),
  L("he", "heb", "עברית", "Hebrew", "hebrew", true),
  L("hi", "hin", "हिन्दी", "Hindi", "devanagari"),
  L("mr", "mar", "मराठी", "Marathi", "devanagari"),
  L("ne", "nep", "नेपाली", "Nepali", "devanagari"),
  L("bn", "ben", "বাংলা", "Bengali", "bengali"),
  L("ta", "tam", "தமிழ்", "Tamil", "tamil"),
  L("te", "tel", "తెలుగు", "Telugu", "telugu"),
  L("kn", "kan", "ಕನ್ನಡ", "Kannada", "kannada"),
  L("ml", "mal", "മലയാളം", "Malayalam", "malayalam"),
  L("gu", "guj", "ગુજરાતી", "Gujarati", "gujarati"),
  L("pa", "pan", "ਪੰਜਾਬੀ", "Punjabi", "gurmukhi"),
  L("or", "ori", "ଓଡ଼ିଆ", "Odia", "oriya"),
  L("si", "sin", "සිංහල", "Sinhala", "sinhala"),
  L("th", "tha", "ไทย", "Thai", "thai"),
  L("lo", "lao", "ລາວ", "Lao", "lao"),
  L("km", "khm", "ខ្មែរ", "Khmer", "khmer"),
  L("my", "mya", "မြန်မာ", "Burmese", "myanmar"),
  L("ka", "kat", "ქართული", "Georgian", "georgian"),
  L("hy", "hye", "Հայերեն", "Armenian", "armenian"),
  L("am", "amh", "አማርኛ", "Amharic", "ethiopic"),
  L("zh", "zho", "中文（简体）", "Chinese (Simplified)", "han-sc"),
  L("zh-Hant", "zho", "中文（繁體）", "Chinese (Traditional)", "han-tc"),
  L("ja", "jpn", "日本語", "Japanese", "japanese"),
  L("ko", "kor", "한국어", "Korean", "korean"),
];

/** Other names a speech model or a profile may give a language. */
const ALIASES: Record<string, string> = {
  norwegian: "nb", no: "nb", nob: "nb", nno: "nb", tagalog: "fil", tgl: "fil", burmese: "my", flemish: "nl",
  iw: "he", in: "id", jw: "id",
};

const byCode = new Map(LANGUAGES.map((l) => [l.code.toLowerCase(), l]));
const byIso3 = new Map(LANGUAGES.map((l) => [l.iso3, l]));
const byEnglish = new Map(LANGUAGES.map((l) => [l.english.toLowerCase().replace(/ \(.*\)$/, ""), l]));

const CHINESE = new Set(["zh", "zho", "cmn", "chinese", "mandarin", "yue", "cantonese"]);

/**
 * Whatever a speech model or a form calls a language, as the room's code: "es", "spa", "Spanish", "pt_br" and
 * "zh-TW" all land on a code. A valid two or three letter code the list does not know is kept as it is, since
 * the room must work in any language. Null for anything that is not a language.
 */
export function normalizeLanguage(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim().replace(/_/g, "-");
  if (!text || text.length > 40) return null;
  const parts = text.split("-");
  const base = (parts[0] ?? "").toLowerCase();
  const rest = parts.slice(1);
  const script = rest.find((p) => /^[A-Za-z]{4}$/.test(p));
  const region = rest.find((p) => /^[A-Za-z]{2}$/.test(p));

  if (CHINESE.has(base)) {
    const traditional =
      base === "yue" || base === "cantonese" || script?.toLowerCase() === "hant" || ["tw", "hk", "mo"].includes((region ?? "").toLowerCase());
    return traditional ? "zh-Hant" : "zh";
  }
  const found = byCode.get(ALIASES[base] ?? base) ?? byIso3.get(base) ?? byEnglish.get(base);
  if (found) return region ? `${found.code}-${region.toUpperCase()}` : found.code;
  if (/^[a-z]{2,3}$/.test(base) && rest.length === 0) return base;
  return null;
}

const info = (code: string): Language | undefined =>
  byCode.get(code.toLowerCase()) ?? byCode.get((code.split("-")[0] ?? "").toLowerCase());

/** The language in its own words, for the host's select: "Español", "தமிழ்". A tag the list lacks reads by Intl. */
export function languageName(code: string): string {
  const known = byCode.get(code.toLowerCase());
  if (known) return known.own;
  try {
    const own = new Intl.DisplayNames([code], { type: "language" }).of(code);
    if (own && own !== code) return own.charAt(0).toLocaleUpperCase(code) + own.slice(1);
  } catch {
    // A tag Intl does not know falls through to the list or the code.
  }
  return info(code)?.own ?? code;
}

/** The language's name in English, for a model that is told what to write in. */
export function englishName(code: string): string {
  const known = byCode.get(code.toLowerCase());
  if (known) return known.english;
  try {
    const name = new Intl.DisplayNames(["en"], { type: "language" }).of(code);
    if (name && name !== code) return name;
  } catch {
    // A tag Intl does not know falls through to the list or the code.
  }
  return info(code)?.english ?? code;
}

/** Arabic, Hebrew, Persian and Urdu read right to left. */
export function isRtl(code: string): boolean {
  return info(code)?.rtl === true;
}

/** The script a language is written in; a code the list lacks is taken as Latin. */
export function scriptOf(code: string): Script {
  return info(code)?.script ?? "latin";
}

/** The Google Fonts family that draws each script Plus Jakarta Sans does not. */
const NOTO: Partial<Record<Script, string>> = {
  cyrillic: "Noto Sans",
  greek: "Noto Sans",
  arabic: "Noto Sans Arabic",
  hebrew: "Noto Sans Hebrew",
  devanagari: "Noto Sans Devanagari",
  bengali: "Noto Sans Bengali",
  tamil: "Noto Sans Tamil",
  telugu: "Noto Sans Telugu",
  kannada: "Noto Sans Kannada",
  malayalam: "Noto Sans Malayalam",
  gujarati: "Noto Sans Gujarati",
  gurmukhi: "Noto Sans Gurmukhi",
  oriya: "Noto Sans Oriya",
  sinhala: "Noto Sans Sinhala",
  thai: "Noto Sans Thai",
  lao: "Noto Sans Lao",
  khmer: "Noto Sans Khmer",
  myanmar: "Noto Sans Myanmar",
  georgian: "Noto Sans Georgian",
  armenian: "Noto Sans Armenian",
  ethiopic: "Noto Sans Ethiopic",
  "han-sc": "Noto Sans SC",
  "han-tc": "Noto Sans TC",
  japanese: "Noto Sans JP",
  korean: "Noto Sans KR",
};

/** The family that draws this language's script, or null when Plus Jakarta Sans already does. */
export function notoFamilyFor(code: string): string | null {
  return NOTO[scriptOf(code)] ?? null;
}

// --- the fixed words the page needs ---------------------------------------
/** The form's fixed labels and its button. */
export interface FormWords {
  first: string;
  last: string;
  email: string;
  phone: string;
  save: string;
}

const F = (first: string, last: string, email: string, phone: string, save: string): FormWords => ({ first, last, email, phone, save });

const EN_FORM = F("First name", "Last name", "Email", "Phone", "Save my seat");

/**
 * Written once for the languages most rooms are in. A language not here gets `null`, and the page model is
 * told to write these words itself in the room's language, so no room ever shows another language's form.
 */
const FORM: Record<string, FormWords> = {
  en: EN_FORM,
  es: F("Nombre", "Apellido", "Correo electrónico", "Teléfono", "Reservar mi lugar"),
  fr: F("Prénom", "Nom", "E-mail", "Téléphone", "Réserver ma place"),
  de: F("Vorname", "Nachname", "E-Mail", "Telefon", "Meinen Platz sichern"),
  pt: F("Nome", "Sobrenome", "E-mail", "Telefone", "Garantir minha vaga"),
  it: F("Nome", "Cognome", "E-mail", "Telefono", "Riserva il mio posto"),
  nl: F("Voornaam", "Achternaam", "E-mail", "Telefoon", "Reserveer mijn plek"),
  sv: F("Förnamn", "Efternamn", "E-post", "Telefon", "Reservera min plats"),
  pl: F("Imię", "Nazwisko", "E-mail", "Telefon", "Zarezerwuj miejsce"),
  tr: F("Ad", "Soyad", "E-posta", "Telefon", "Yerimi ayırt"),
  ru: F("Имя", "Фамилия", "Электронная почта", "Телефон", "Занять место"),
  uk: F("Ім'я", "Прізвище", "Електронна пошта", "Телефон", "Зайняти місце"),
  ar: F("الاسم الأول", "اسم العائلة", "البريد الإلكتروني", "الهاتف", "احجز مقعدي"),
  he: F("שם פרטי", "שם משפחה", "אימייל", "טלפון", "שמרו לי מקום"),
  fa: F("نام", "نام خانوادگی", "ایمیل", "تلفن", "جای من را رزرو کن"),
  ur: F("پہلا نام", "آخری نام", "ای میل", "فون", "میری نشست محفوظ کریں"),
  hi: F("पहला नाम", "अंतिम नाम", "ईमेल", "फ़ोन", "मेरी सीट सुरक्षित करें"),
  bn: F("প্রথম নাম", "শেষ নাম", "ইমেইল", "ফোন", "আমার আসন সংরক্ষণ করুন"),
  ta: F("முதல் பெயர்", "கடைசி பெயர்", "மின்னஞ்சல்", "தொலைபேசி", "என் இடத்தை பதிவு செய்"),
  ja: F("名", "姓", "メールアドレス", "電話番号", "席を予約する"),
  zh: F("名", "姓", "电子邮箱", "电话", "预留我的席位"),
  "zh-Hant": F("名", "姓", "電子郵件", "電話", "預留我的席位"),
  ko: F("이름", "성", "이메일", "전화번호", "내 자리 예약하기"),
  th: F("ชื่อ", "นามสกุล", "อีเมล", "โทรศัพท์", "จองที่นั่งของฉัน"),
  vi: F("Tên", "Họ", "Email", "Điện thoại", "Giữ chỗ của tôi"),
  id: F("Nama depan", "Nama belakang", "Email", "Telepon", "Amankan tempat saya"),
};

/** The fixed form words for a room's language, or null when the page model has to write them. */
export function formWords(code: string): FormWords | null {
  return FORM[code] ?? FORM[(code.split("-")[0] ?? "")] ?? null;
}

/** The sentences a live's page says in its own words. `{when}` and `{date}` stand for the time and the day. */
interface LiveWords {
  liveNow: string;
  sameTime: string;
  yourTime: string;
  replay: string;
}

const W = (liveNow: string, sameTime: string, yourTime: string, replay: string): LiveWords => ({ liveNow, sameTime, yourTime, replay });

const LIVE: Record<string, LiveWords> = {
  es: W("En vivo ahora. Regístrate y entras directo.", "Es tu hora también.", "{when}, en tu hora", "Esto fue en vivo el {date}. Regístrate para ver la repetición."),
  fr: W("En direct maintenant. Inscrivez-vous et entrez tout de suite.", "C'est aussi votre heure.", "{when}, heure locale", "C'était en direct le {date}. Inscrivez-vous pour voir le replay."),
  de: W("Jetzt live. Melde dich an und sei sofort dabei.", "Das ist auch deine Zeit.", "{when}, in deiner Zeit", "Das war live am {date}. Melde dich an, um die Aufzeichnung zu sehen."),
  pt: W("Ao vivo agora. Inscreva-se e entre direto.", "É o seu horário também.", "{when}, no seu horário", "Isto foi ao vivo em {date}. Inscreva-se para ver a gravação."),
  it: W("In diretta ora. Iscriviti e entra subito.", "È anche la tua ora.", "{when}, nel tuo fuso orario", "Era in diretta il {date}. Iscriviti per vedere la replica."),
  nl: W("Nu live. Schrijf je in en je zit er meteen bij.", "Dat is ook jouw tijd.", "{when}, jouw tijd", "Dit was live op {date}. Schrijf je in om de herhaling te bekijken."),
  ru: W("Прямой эфир идёт. Зарегистрируйтесь и сразу заходите.", "Это и ваше время.", "{when}, по вашему времени", "Эфир прошёл {date}. Зарегистрируйтесь, чтобы посмотреть запись."),
  ar: W("البث مباشر الآن. سجّل وادخل فورًا.", "هذا وقتك أيضًا.", "{when} بتوقيتك", "كان هذا مباشرًا في {date}. سجّل لمشاهدة الإعادة."),
  hi: W("अभी लाइव है। रजिस्टर करें और सीधे जुड़ें।", "यह आपका समय भी है।", "{when}, आपके समय में", "यह {date} को लाइव था। रिप्ले देखने के लिए रजिस्टर करें।"),
  ja: W("ただいまライブ中です。登録してすぐに参加できます。", "あなたの時間でもあります。", "{when}（あなたの時間）", "{date}にライブで行われました。登録して録画をご覧ください。"),
  zh: W("正在直播。注册即可立即加入。", "这也是你的时间。", "{when}（你的时间）", "本场直播于{date}进行。注册后即可观看回放。"),
  ko: W("지금 라이브 중입니다. 등록하면 바로 입장합니다.", "당신의 시간과 같습니다.", "{when} (내 시간)", "{date}에 라이브로 진행되었습니다. 등록하고 다시 보기를 시청하세요."),
  tr: W("Şu anda canlı. Kaydolun ve hemen katılın.", "Bu sizin saatiniz de.", "{when}, sizin saatinizle", "Bu, {date} tarihinde canlıydı. Kaydolun ve tekrarı izleyin."),
  id: W("Sedang tayang langsung. Daftar dan langsung masuk.", "Itu waktu Anda juga.", "{when}, waktu Anda", "Ini tayang langsung pada {date}. Daftar untuk menonton ulangannya."),
};

/** A live page's own sentences for a language, or null when the page says nothing there rather than English. */
export function liveWords(code: string): LiveWords | null {
  return LIVE[code] ?? LIVE[code.split("-")[0] ?? ""] ?? null;
}

// --- counting words -------------------------------------------------------
/**
 * Words in a piece of text, for any script: `Intl.Segmenter` at word level, counting only the pieces that are
 * words. A plain split on spaces counts a whole Chinese, Japanese or Thai sentence as one word.
 */
export function countWords(text: string, language: string = DEFAULT_LANGUAGE): number {
  try {
    const seg = new Intl.Segmenter(language, { granularity: "word" });
    let n = 0;
    for (const s of seg.segment(text)) if (s.isWordLike) n += 1;
    return n;
  } catch {
    return text.split(/\s+/).filter(Boolean).length;
  }
}

/** The text's words as a list, for the checks that compare words one by one. */
export function wordsOf(text: string, language: string = DEFAULT_LANGUAGE): string[] {
  try {
    const seg = new Intl.Segmenter(language, { granularity: "word" });
    const out: string[] = [];
    for (const s of seg.segment(text)) if (s.isWordLike) out.push(s.segment);
    return out;
  } catch {
    return text.split(/\s+/).filter(Boolean);
  }
}

// --- direction of a written text ------------------------------------------
/** Arabic, Hebrew, Syriac, Thaana and N'Ko letters, and their presentation forms: they run right to left. */
const RTL_LETTER = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFC]/;
const OTHER_LETTER = /\p{L}/u;

/**
 * The direction a body of text reads in: the side with more letters. The first letter would do for a short line, but a
 * whole email that opens with a Latin name or brand and then runs in Arabic is an Arabic email. A tie reads left to right.
 */
export function textDirection(text: string): "rtl" | "ltr" {
  let rtl = 0;
  let other = 0;
  for (const ch of text) {
    if (RTL_LETTER.test(ch)) rtl += 1;
    else if (OTHER_LETTER.test(ch)) other += 1;
  }
  return rtl > other ? "rtl" : "ltr";
}

// --- length as a reader sees it -------------------------------------------
/**
 * Visible letters: what a reader counts. `.length` counts UTF-16 units, and one Tamil or Hindi letter is two or three
 * of them, so a cap of 120 would allow about 50 letters. A grapheme is what the eye takes as one character.
 */
export function visibleLength(text: string): number {
  try {
    let n = 0;
    for (const _ of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text)) n += 1;
    return n;
  } catch {
    return text.length;
  }
}

/** The first `max` visible letters of a text, never cutting a letter in two. */
export function sliceVisible(text: string, max: number): string {
  if (text.length <= max) return text;
  try {
    let out = "";
    let n = 0;
    for (const part of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text)) {
      if (n >= max) break;
      out += part.segment;
      n += 1;
    }
    return out;
  } catch {
    return text.slice(0, max);
  }
}
